import React, { useEffect, useState } from "react";
import { GraduationCap, CheckCircle2, Clock, XCircle, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export const TeacherVerify = () => {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ school_name: "", school_email: "", teacher_id: "", subject_taught: "", grade_level: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/teacher/status"); setStatus(data); } catch {}
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/teacher/verify", form);
      toast.success("Verification submitted — we'll review shortly!");
      await refresh(); await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  if (!user) return <div className="max-w-2xl mx-auto px-6 py-20 text-center">Please <a href="/login" className="text-indigo-600 font-semibold">sign in</a> first.</div>;

  const s = status?.status;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="p-8 rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white mb-8">
        <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs font-semibold mb-3">
          <GraduationCap className="w-3 h-3" /> Teacher perks
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Verified Teacher program</h1>
        <p className="mt-2 text-white/85 max-w-xl">Get an automatic <strong>15% off every retail order</strong>, plus priority handling on all your wholesale quotes.</p>
      </div>

      {status?.is_verified_teacher ? (
        <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-4" data-testid="teacher-verified-banner">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 shrink-0" />
          <div>
            <div className="font-display font-bold text-emerald-900 text-lg">You're a verified teacher!</div>
            <div className="text-sm text-emerald-800 mt-1">Your 15% discount is now automatically applied at checkout. Your wholesale quotes get priority handling.</div>
          </div>
        </div>
      ) : s === "pending" ? (
        <div className="p-6 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-4" data-testid="teacher-pending-banner">
          <Clock className="w-10 h-10 text-amber-600 shrink-0" />
          <div>
            <div className="font-display font-bold text-amber-900 text-lg">Under review</div>
            <div className="text-sm text-amber-800 mt-1">We're reviewing your submission. Typical review time is under 24 hours. You'll see the discount applied automatically once approved.</div>
            <div className="text-xs text-amber-700 mt-2">Submitted: <span className="font-mono">{status?.verification?.school_name}</span></div>
          </div>
        </div>
      ) : s === "rejected" ? (
        <div className="p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4 mb-6">
          <XCircle className="w-10 h-10 text-red-600 shrink-0" />
          <div>
            <div className="font-display font-bold text-red-900 text-lg">Not verified this time</div>
            <div className="text-sm text-red-800 mt-1">Please double-check your school details and resubmit below.</div>
          </div>
        </div>
      ) : null}

      {(!status?.is_verified_teacher && s !== "pending") && (
        <form onSubmit={submit} className="p-6 bg-white border border-slate-200 rounded-2xl mt-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 uppercase tracking-wider"><Sparkles className="w-4 h-4 text-indigo-600" />Submit your details</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>School / Institution name</Label><Input required value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} data-testid="teacher-school-input" /></div>
            <div><Label>Official school email</Label><Input required type="email" value={form.school_email} onChange={(e) => setForm({ ...form, school_email: e.target.value })} placeholder="you@school.edu" data-testid="teacher-school-email-input" /></div>
            <div><Label>Teacher ID (optional)</Label><Input value={form.teacher_id} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })} data-testid="teacher-id-input" /></div>
            <div><Label>Subject taught</Label><Input value={form.subject_taught} onChange={(e) => setForm({ ...form, subject_taught: e.target.value })} placeholder="Math, Art, English…" data-testid="teacher-subject-input" /></div>
            <div className="sm:col-span-2"><Label>Grade level</Label><Input value={form.grade_level} onChange={(e) => setForm({ ...form, grade_level: e.target.value })} placeholder="e.g. Kindergarten, 3rd Grade, High School" data-testid="teacher-grade-input" /></div>
          </div>
          <Button type="submit" disabled={busy} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="teacher-submit-btn">
            {busy ? "Submitting…" : "Submit for verification"}
          </Button>
        </form>
      )}

      <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm">
        <div className="p-5 bg-white rounded-2xl border border-slate-200">
          <div className="font-display font-bold">15% off retail</div>
          <div className="text-slate-600 mt-1">Auto-applied to every non-wholesale item, forever.</div>
        </div>
        <div className="p-5 bg-white rounded-2xl border border-slate-200">
          <div className="font-display font-bold">Priority quotes</div>
          <div className="text-slate-600 mt-1">Your wholesale requests jump to the top of the admin queue.</div>
        </div>
        <div className="p-5 bg-white rounded-2xl border border-slate-200">
          <div className="font-display font-bold">Verified badge</div>
          <div className="text-slate-600 mt-1">Show your Verified Teacher badge in your account.</div>
        </div>
      </div>
    </div>
  );
};
